import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/client';
import { PlacedOrder } from '../types';
import NotificationService from '../services/NotificationService';
import { useData } from '../context/DataContext';

interface DriverRatingModalProps {
    order: PlacedOrder;
    onClose: () => void;
    onSuccess: () => void;
    isRestaurant?: boolean;
}

const DriverRatingModal: React.FC<DriverRatingModalProps> = ({ order, onClose, onSuccess, isRestaurant = false }) => {
    const { updateRestaurantRating, restaurants } = useData();
    const [rating, setRating] = useState(0); // Driver rating
    const [restaurantRating, setRestaurantRating] = useState(0); // Restaurant rating
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fireNotification = async (finalRestoRating: number) => {
        try {
            const restoData = restaurants.find(r => r.id === order.restaurantId);
            const ownerId = restoData?.ownerId;
            
            if (ownerId) {
                const title = "Commande Terminée ! ✅";
                const body = finalRestoRating > 0 
                    ? `${order.customerName} a confirmé avoir reçu sa commande et vous a laissé une note de ${finalRestoRating}/5.`
                    : `${order.customerName} a confirmé avoir reçu sa commande.`;
                
                await NotificationService.notifyRestaurant(ownerId, title, body, { orderId: order.id });
            }
        } catch (err) {
            console.error("Error sending receipt notification to restaurant:", err);
        }
    };

    const handleSubmit = async () => {
        // Validation: If it's a client rating, we prefer they rate the driver if one is assigned
        if (!isRestaurant && order.livreurId && rating === 0) {
            alert("Veuillez donner une note au livreur.");
            return;
        }

        setIsSubmitting(true);
        try {
            // Updated logic: We ONLY update the order document.
            // Cloud Functions will detect this change and update the restaurant/driver averages securely.
            // This bypasses Firestore security rule restrictions for clients.
            const orderRef = doc(db, 'orders', order.id);
            const updates: any = {};
            
            if (isRestaurant) {
                // Restaurant rating the driver
                if (rating > 0) updates.restaurantDriverRating = rating;
            } else {
                // Client rating the order - Always set a value to indicator completion
                updates.driverRating = rating || 0;
                updates.restaurantRating = restaurantRating || 0;
            }

            if (Object.keys(updates).length > 0) {
                await updateDoc(orderRef, updates);
            }

            // Notify restaurant owner
            if (!isRestaurant) {
                await fireNotification(restaurantRating);
            }

            onSuccess();
        } catch (error) {
            console.error("Error submitting rating:", error);
            alert("Une erreur est survenue lors de l'enregistrement de votre avis.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSkip = async () => {
        setIsSubmitting(true);
        try {
            if (!isRestaurant) {
                // Mark as rated (0) so it leaves "En cours"
                const orderRef = doc(db, 'orders', order.id);
                await updateDoc(orderRef, {
                    restaurantRating: 0,
                    driverRating: 0
                });
                await fireNotification(0);
            }
            onSuccess();
        } catch (err) {
            console.error("Error skipping rating:", err);
            onSuccess(); // Still proceed
        } finally {
            setIsSubmitting(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
            {/* Backdrop click to close */}
            <div className="absolute inset-0 z-0" onClick={onClose} />

            <div className="relative z-10 bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden flex flex-col items-center p-8 space-y-6 font-inter text-center animate-scale-in">

                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-3xl">🎉</span>
                </div>

                <h2 className="text-2xl font-black text-gray-900 leading-tight">Merci pour votre commande !</h2>
                
                <div className="w-full space-y-6">
                    {/* Section Restaurant */}
                    {!isRestaurant && (
                        <div className="space-y-3">
                            <p className="text-xs font-black text-gray-400 uppercase tracking-[0.2em]">Notez le plat & restaurant</p>
                            <p className="text-sm font-bold text-gray-800">{order.restaurantName}</p>
                            <div className="flex justify-center gap-2">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <button
                                        key={`resto-${star}`}
                                        onClick={() => setRestaurantRating(star)}
                                        className={`text-3xl transition-transform active:scale-90 ${restaurantRating >= star ? 'text-red-500' : 'text-gray-200'} hover:scale-110`}
                                    >
                                        ★
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Section Livreur */}
                    {order.livreurId && (
                        <div className="space-y-3 pt-2 border-t border-gray-50">
                            <p className="text-xs font-black text-gray-400 uppercase tracking-[0.2em]">{isRestaurant ? "Notez le livreur" : "Notez la livraison"}</p>
                            <p className="text-sm font-bold text-gray-800">{order.assignedDriver?.name || 'Livreur'}</p>
                            <div className="flex justify-center gap-2">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <button
                                        key={`driver-${star}`}
                                        onClick={() => setRating(star)}
                                        className={`text-3xl transition-transform active:scale-90 ${rating >= star ? 'text-yellow-400' : 'text-gray-200'} hover:scale-110`}
                                    >
                                        ★
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="w-full space-y-3 pt-4">
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting || (!rating && !restaurantRating)}
                        className={`w-full py-3.5 rounded-2xl font-bold transition-all flex items-center justify-center gap-2 
                            ${(rating > 0 || restaurantRating > 0) && !isSubmitting ? 'bg-black text-white hover:bg-gray-900 shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
                    >
                        {isSubmitting ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                Envoi...
                            </>
                        ) : (
                            'Envoyer mon avis'
                        )}
                    </button>

                    <button
                        onClick={handleSkip} // Pass without rating
                        disabled={isSubmitting}
                        className="w-full py-3 rounded-2xl font-semibold text-gray-500 hover:bg-gray-50 transition-colors"
                    >
                        Passer
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default DriverRatingModal;
